import { createSelector } from 'reselect';
import { MVPD_PREVIOUS_KEY } from 'constants';
import { AuthTypes, AuthStates, Flags } from 'enums';
import { get } from 'lib/util/localStorage';
import canUseDOM from 'lib/util/canUseDOM';
import bootstrapSelector from './bootstrapSelector';
import { roadblockSelector, bypassTokenSelector } from './appSelector';
import routingSelector from './routingSelector';

// TODO: move this to `lib` once it's used in other places
export function convertToSecureImage(provider) {
    if (!provider || !provider.images) {
        return provider;
    }
    return Object.keys(provider.images).reduce((acc, key) => {
        const image = provider.images[key];
        if (image && image.href) {
            acc.images[key] = { href: image.href.replace('http://a.espncdn.com', 'https://secure.espncdn.com') };
        }
        return acc;
    }, provider);
}

export const authenticationEnabledSelector = createSelector(
    roadblockSelector,
    (roadblock) => !roadblock
);

// generic auth selectors
export const authenticationSelector = ({ authentication = {} }) => authentication;

export const userAuthSelector = createSelector(
    authenticationSelector,
    bypassTokenSelector,
    ({ authTypes = [] }, bypassToken) => {
        if (bypassToken) {
            return [AuthTypes.ISP, AuthTypes.MVPD];
        }

        return authTypes;
    }
);

// adobe pass selectors
export const adobePassSelector = createSelector(
    authenticationSelector,
    ({ adobePass = {} }) => adobePass
);

export const adobePassInfoSelector = createSelector(
    adobePassSelector,
    ({ info = null }) => convertToSecureImage(info)
);

export const adobePassMetadataSelector = createSelector(
    adobePassSelector,
    ({ metadata = {} }) => metadata
);

export const adobePassInitializedSelector = createSelector(
    adobePassSelector,
    ({ initialization: { completeTime } = {} }) => !!completeTime
);

export const adobePassAuthenticatedSelector = createSelector(
    adobePassSelector,
    ({ isAuthenticated }) => !!isAuthenticated
);

export const adobePassIsAuthorizedSelector = createSelector(
    adobePassSelector,
    ({ isAuthorized }) => !!isAuthorized
);

export const checkAuthenticationCompleteSelector = createSelector(
    adobePassSelector,
    ({ checkAuthentication: { completeTime } = {} }) => !!completeTime
);

// ip selectors
export const ipSelector = createSelector(
    authenticationSelector,
    ({ ip = {} }) => ip
);

export const ipInfoSelector = createSelector(
    ipSelector,
    ({ info = null }) => convertToSecureImage(info)
);

export const userLocationSelector = createSelector(
    ipSelector,
    ({ location = {} }) => location
);

// bam selectors
export const bamSelector = createSelector(
    authenticationSelector,
    ({ bam = {} }) => bam
);

export const subscriptionsSelector = createSelector(
    bamSelector,
    ({ subscriptions }) => subscriptions
);

export const superBundleSelector = createSelector(
    subscriptionsSelector,
    (subscriptions) => {
        if (subscriptions && subscriptions.length) {
            return subscriptions.filter(sub => sub.isActive)
                .some(sub => {
                    // first test for the existance of the
                    // bundle property and use it's value
                    if ('bundle' in sub) {
                        return sub.bundle;
                    }

                    // fall-back check for categoryCode of superbundle in products
                    return sub.products.some(({ categoryCodes }) => categoryCodes &&
                            Array.isArray(categoryCodes) &&
                            categoryCodes.map(item => item.toLowerCase()).includes('superbundle'));
                });
        }
        return false;
    }
);

export const bamSessionTokenSelector = createSelector(
    bamSelector,
    ({ session: { token = null } = {} }) => token
);

export const bamSubReadySelector = createSelector(
    authenticationSelector,
    ({ bamSubReady = false }) => bamSubReady
);

export const geoLocationSelector = createSelector(
    bamSelector,
    ({ geo = {} }) => geo
);

export const userTrackingSelector = createSelector(
    bamSelector,
    ({ userTracking = {} }) => userTracking
);

export const preauthorizedResourcesSelector = createSelector(
    adobePassSelector,
    ({ preauthorizedResources }) => preauthorizedResources
);

export const entitlementTokenSelector = () => {
    let entitlementToken;
    if (canUseDOM) {
        try {
            entitlementToken = window.localStorage.getItem('entitlementToken');
            if (entitlementToken) {
                entitlementToken = JSON.parse(entitlementToken);
            } else {
                entitlementToken = undefined;
            }
        } catch (err) {
            console.error(err);
        }
    }
    return entitlementToken;
};

export const zipcodeSelector = createSelector(
    geoLocationSelector,
    userLocationSelector,
    ({ zipCode }, { zipcode }) => zipCode || zipcode
);

export const authStatesSelector = createSelector(
    userAuthSelector,
    entitlementTokenSelector,
    (userAuth, entitlementToken = {}) => {
        let authStates;
        const prevMVPDState = get(MVPD_PREVIOUS_KEY);
        const currMVPDState = entitlementToken.mvpd;
        const ispState = userAuth.filter(auth => (auth === AuthTypes.ISP))[0];
        if (prevMVPDState) {
            authStates = [AuthStates.MVPD_PREVIOUS];
        }
        if (currMVPDState) {
            if (!authStates) {
                authStates = [];
            }
            authStates.push(AuthStates.MVPD);
        }
        if (ispState) {
            if (!authStates) {
                authStates = [];
            }
            authStates.push(AuthStates.ISP);
        }
        return authStates;
    }
);

export const passOneIdTokenSelector = createSelector(
    bootstrapSelector,
    routingSelector,
    ({ bam: { passToken: passTokenArg = true } = {} }, { locationBeforeTransitions }) => {
        let passToken = passTokenArg;
        const { query = {} } = locationBeforeTransitions || {};
        if (query[Flags.PASS_ONEID_TOKEN]) {
            passToken = query[Flags.PASS_ONEID_TOKEN] === 'true';
        }
        return passToken;
    }
);



// WEBPACK FOOTER //
// ./app/common/selectors/authenticationSelectors.js